//core
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../../common/user";
import { subAffiliateReportV2Service } from "../../../../services/admin/subAffiliateReportV2.service";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../../common/ui";

interface IObj {
  [i:string]: string;
}

export type GroupByObj = {
  [k: string]: string | IObj
}

type OptionsDataType = {
  groupBy: GroupByObj,
  group_by?: GroupByObj,
  row_on_page: number[],
  separators_for_format: {
    csv: string[];
  },
  encoding_types: string[];
  availableFilters: string[];
  available_filters?: string[];
}

export type subAffiliateReportV2SliceState = {
  options: {
    data: OptionsDataType | null,
    error: any,
    isFetching: boolean,
  },
  list: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  columns: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  download: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  products: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  deals: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  brands: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  currencies: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  platforms: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: subAffiliateReportV2SliceState = {
  options: {
    data: null,
    error: null,
    isFetching: false,
  },
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  columns: {
    data: null,
    error: null,
    isFetching: false,
  },
  download: {
    data: null,
    error: null,
    isFetching: false,
  },
  products: {
    data: null,
    error: null,
    isFetching: false,
  },
  deals: {
    data: null,
    error: null,
    isFetching: false,
  },
  brands: {
    data: null,
    error: null,
    isFetching: false,
  },
  currencies: {
    data: null,
    error: null,
    isFetching: false,
  },
  platforms: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getOptionsData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getOptionsData',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffiliateReportV2Service.getOptions(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getListData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getListData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffiliateReportV2Service.getReport(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        )
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getColumnsData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getColumnsData',
  async (payload, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffiliateReportV2Service.getColumns(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getProductsData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getProductsData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffiliateReportV2Service.getProducts(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getBrandsData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getBrandsData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffiliateReportV2Service.getBrands(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getCurrenciesData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getCurrenciesData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffiliateReportV2Service.getCurrencies(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getPlatformsData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getPlatformsData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffiliateReportV2Service.getPlatforms(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getDealsData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/getDealsData',
  async (_: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffiliateReportV2Service.getDeals();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const setDownloadData: any = createAsyncThunk(
  'adm_sub_affiliate_report_v2/setDownloadData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffiliateReportV2Service.setDownload(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFiltersFieldData: any = createAsyncThunk(
	'adm_sub_affiliate_report_v2/getFiltersFieldData',
	async (payload:any, { rejectWithValue }) => {
		try {
			const response = await subAffiliateReportV2Service.getFiltersField(payload);
			const data = await response.json();
			if (!response.ok) {
				return rejectWithValue(data)
			}

			return data;
		} catch (error) {
			return rejectWithValue(error)
		}
	}
);

//slice
const subAffiliateReportV2Slice = createSlice({
  name: 'adm_sub_affiliate_report_v2',
  initialState: initialState,
  reducers: {
    reset: () => initialState,
    clearListState(state) {
      state.list.data = null;
      state.list.error = null;
      state.list.isFetching = false;
    },
    clearColumnsState(state) {
      state.columns.data = null;
      state.columns.error = null;
      state.columns.isFetching = false;
    },
    clearDownload(state) {
      state.download.data = null;
      state.download.error = null;
      state.download.isFetching = false;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<subAffiliateReportV2SliceState>>) => {
    builder.addCase(getOptionsData.pending, (state) => {
      state.options.isFetching = true;
      state.options.error = null;
    });
    builder.addCase(getOptionsData.fulfilled, (state, action) => {
      state.options.error = null;
      state.options.isFetching = false;
      state.options.data = action.payload;
    });
    builder.addCase(getOptionsData.rejected, (state, action) => {
      state.options.isFetching = false;
      state.options.error = action.payload;
    });

    builder.addCase(getListData.pending, (state) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state, action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state, action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
    });

    builder.addCase(getColumnsData.pending, (state) => {
      state.columns.isFetching = true;
      state.columns.error = null;
    });
    builder.addCase(getColumnsData.fulfilled, (state, action) => {
      state.columns.error = null;
      state.columns.isFetching = false;
      state.columns.data = action.payload;
    });
    builder.addCase(getColumnsData.rejected, (state, action) => {
      state.columns.isFetching = false;
      state.columns.error = action.payload;
    });

    builder.addCase(setDownloadData.pending, (state) => {
      state.download.isFetching = true;
      state.download.error = null;
    });
    builder.addCase(setDownloadData.fulfilled, (state, action) => {
      state.download.error = null;
      state.download.isFetching = false;
      state.download.data = action.payload;
    });
    builder.addCase(setDownloadData.rejected, (state, action) => {
      state.download.isFetching = false;
      state.download.error = action.payload;
    });

    builder.addCase(getDealsData.pending, (state) => {
      state.deals.isFetching = true;
      state.deals.error = null;
    });
    builder.addCase(getDealsData.fulfilled, (state, action) => {
      state.deals.error = null;
      state.deals.isFetching = false;
      state.deals.data = action.payload;
    });
    builder.addCase(getDealsData.rejected, (state, action) => {
      state.deals.isFetching = false;
      state.deals.error = action.payload;
    });

    builder.addCase(getProductsData.pending, (state) => {
      state.products.isFetching = true;
      state.products.error = null;
    });
    builder.addCase(getProductsData.fulfilled, (state, action) => {
      state.products.error = null;
      state.products.isFetching = false;
      state.products.data = action.payload;
    });
    builder.addCase(getProductsData.rejected, (state, action) => {
      state.products.isFetching = false;
      state.products.error = action.payload;
    });

    builder.addCase(getBrandsData.pending, (state) => {
      state.brands.isFetching = true;
      state.brands.error = null;
    });
    builder.addCase(getBrandsData.fulfilled, (state, action) => {
      state.brands.error = null;
      state.brands.isFetching = false;
      state.brands.data = action.payload;
    });
    builder.addCase(getBrandsData.rejected, (state, action) => {
      state.brands.isFetching = false;
      state.brands.error = action.payload;
    });

    builder.addCase(getCurrenciesData.pending, (state) => {
      state.currencies.isFetching = true;
      state.currencies.error = null;
    });
    builder.addCase(getCurrenciesData.fulfilled, (state, action) => {
      state.currencies.error = null;
      state.currencies.isFetching = false;
      state.currencies.data = action.payload;
    });
    builder.addCase(getCurrenciesData.rejected, (state, action) => {
      state.currencies.isFetching = false;
      state.currencies.error = action.payload;
    });

    builder.addCase(getPlatformsData.pending, (state) => {
      state.platforms.isFetching = true;
      state.platforms.error = null;
    });
    builder.addCase(getPlatformsData.fulfilled, (state, action) => {
      state.platforms.error = null;
      state.platforms.isFetching = false;
      state.platforms.data = action.payload;
    });
    builder.addCase(getPlatformsData.rejected, (state, action) => {
      state.platforms.isFetching = false;
      state.platforms.error = action.payload;
    });
  },
});

export default subAffiliateReportV2Slice.reducer;

export const {
  clearDownload,
  clearListState,
  clearColumnsState,
  reset,
} = subAffiliateReportV2Slice.actions;
